import * as React from 'react';

import {PaginationState} from 'interfaces';

import {Page} from './components';

export interface PaginationProps {
  onClick?: (number) => any;
}

interface State {
  pages: {
    isCurrent: boolean;
    number: number;
    url: string;
  }[];
  showLeftHellip: boolean;
  showRightHellip: boolean;
  urlFirst: string | null;
  urlLast: string | null;
  urlNext: string | null;
  urlPrev: string | null;
}

const NUM_LINKS = 4;

export class Pagination extends React.PureComponent<PaginationProps & PaginationState, State> {
  static getDerivedStateFromProps(props) {
    const {currentPage, totalPages} = props;

    return {
      pages: Pagination.getPageNumbersForRender(props).map((page) => ({
        isCurrent: page === currentPage,
        number: page,
        url: Pagination.getUrl(props, page),
      })),
      showLeftHellip: currentPage > NUM_LINKS + 1,
      showRightHellip: currentPage + NUM_LINKS < totalPages,
      urlFirst: currentPage > 1 ? Pagination.getUrl(props, 1) : null,
      urlLast: currentPage < totalPages ? Pagination.getUrl(props, totalPages) : null,
      urlNext: currentPage < totalPages - 1 ? Pagination.getUrl(props, currentPage + 1) : null,
      urlPrev: currentPage > 2 ? Pagination.getUrl(props, currentPage - 1) : null,
    };
  }

  static getUrl(props, page) {
    const {pathname, search} = props;

    const path = `${pathname}${search}`;
    if (search && search.indexOf('page=') > -1) {
      return path.replace(/(page=)[^&]+/, `$1${page}`);
    } else {
      return `${path}${search ? '&' : '?'}page=${page}`;
    }
  }

  static getPageNumbersForRender(props) {
    const {currentPage, totalPages} = props;

    const pageNumbers: any = [];
    let index = currentPage - NUM_LINKS;
    while (index < currentPage) {
      if (index >= 1) {
        pageNumbers.push(index);
      }

      index++;
    }

    pageNumbers.push(currentPage);

    index = currentPage + 1;
    while (index <= currentPage + NUM_LINKS && index <= totalPages) {
      pageNumbers.push(index);
      index++;
    }

    return pageNumbers;
  }

  constructor(props) {
    super(props);
    this.state = Pagination.getDerivedStateFromProps(props);
  }

  render() {
    const {currentPage, totalPages, onClick} = this.props;

    const {
      pages,
      showLeftHellip,
      showRightHellip,
      urlFirst,
      urlLast,
      urlNext,
      urlPrev,
    } = this.state;

    return (
      <nav className="pagination">
        <div className="pagination--mobile">
          <div>
            {urlFirst && (
              <Page href={urlFirst} key="firstPage" mode="first" onClick={onClick} pageNumber={1} />
            )}

            {urlPrev && (
              <Page
                href={urlPrev}
                key="prevPage"
                mode="prev"
                onClick={onClick}
                pageNumber={currentPage - 1}
              />
            )}
          </div>
          <div>
            {urlNext && (
              <Page
                key="nextPage"
                href={urlNext}
                mode="next"
                onClick={onClick}
                pageNumber={currentPage + 1}
              />
            )}

            {urlLast && (
              <Page
                key="lastPage"
                href={urlLast}
                mode="last"
                onClick={onClick}
                pageNumber={totalPages}
              />
            )}
          </div>
        </div>
        {urlFirst && (
          <Page
            className="first"
            href={urlFirst}
            key="firstPage"
            mode="first"
            onClick={onClick}
            pageNumber={1}
          />
        )}

        {urlPrev && (
          <Page
            className="previous"
            href={urlPrev}
            key="prevPage"
            mode="prev"
            onClick={onClick}
            pageNumber={currentPage - 1}
          />
        )}

        {showLeftHellip && (
          <span className="page gap" key="lefHellip">
            &hellip;
          </span>
        )}

        {pages.map((page) => {
          return (
            <Page
              className="page current"
              href={page.url}
              key={`page-${page.number}`}
              mode={page.isCurrent ? 'current' : null}
              onClick={onClick}
              pageNumber={page.number}
            >
              {page.number}
            </Page>
          );
        })}

        {showRightHellip && (
          <span className="page gap" key="rightHellip">
            &hellip;
          </span>
        )}

        {urlNext && (
          <Page
            className="next"
            key="nextPage"
            href={urlNext}
            mode="next"
            onClick={onClick}
            pageNumber={currentPage + 1}
          />
        )}

        {urlLast && (
          <Page
            className="last"
            key="lastPage"
            href={urlLast}
            mode="last"
            onClick={onClick}
            pageNumber={totalPages}
          />
        )}
      </nav>
    );
  }
}

export default Pagination;
